import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'

// Import views
import LoginView from '../views/LoginView.vue'
import DashboardView from '../views/DashboardView.vue'
import ProductsView from '../views/ProductsView.vue'
import CategoriesView from '../views/CategoriesView.vue'
import CategoryDetailView from '../views/CategoryDetailView.vue'
import InvoicesView from '../views/InvoicesView.vue'
import PaymentsView from '../views/PaymentsView.vue'
import CustomersView from '../views/CustomersView.vue'
import CustomerLedgerView from '../views/CustomerLedgerView.vue'
import SettingsView from '../views/SettingsView.vue'
import POSView from '../views/POSView.vue'
import ReportsView from '../views/ReportsView.vue'
import VendorsView from '../views/VendorsView.vue'
import VendorLedgerView from '../views/VendorLedgerView.vue'
import InvoiceDetailView from '../views/InvoiceDetailView.vue'
import PurchaseBillsView from '../views/PurchaseBillsView.vue'
import PurchaseBillDetailView from '../views/PurchaseBillDetailView.vue'
import QuotationsView from '../views/QuotationsView.vue'
import QuotationDetailView from '../views/QuotationDetailView.vue'
import DebitNotesView from '../views/DebitNotesView.vue'
import CreditNotesView from '../views/CreditNotesView.vue'

const NoAccessView = {
  template: `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui">
      <div style="text-align:center;max-width:400px;padding:2rem">
        <h2 style="margin:0 0 0.5rem;color:#1a1a1a">Module Not Available</h2>
        <p style="color:#666;margin:0 0 1.5rem">Your account does not have access to the Billing module. Please contact your administrator.</p>
        <button onclick="window.history.back()" style="padding:8px 20px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer;font-size:14px">Go Back</button>
      </div>
    </div>
  `,
}

const routes = [
  {
    path: '/no-access',
    name: 'no-access',
    component: NoAccessView,
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: '/products',
    name: 'products',
    component: ProductsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/categories',
    name: 'categories',
    component: CategoriesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/categories/:id',
    name: 'category-detail',
    component: CategoryDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: InvoicesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/invoices/:id',
    name: 'invoice-detail',
    component: InvoiceDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/payments',
    name: 'payments',
    component: PaymentsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/customers',
    name: 'customers',
    component: CustomersView,
    meta: { requiresAuth: true },
  },
  {
    path: '/customers/:id/ledger',
    name: 'customer-ledger',
    component: CustomerLedgerView,
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/pos',
    name: 'pos',
    component: POSView,
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'reports',
    component: ReportsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/vendors',
    name: 'vendors',
    component: VendorsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/vendors/:id/ledger',
    name: 'vendor-ledger',
    component: VendorLedgerView,
    meta: { requiresAuth: true },
  },
  {
    path: '/quotations',
    name: 'quotations',
    component: QuotationsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/quotations/:id',
    name: 'quotation-detail',
    component: QuotationDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/purchase-bills',
    name: 'purchase-bills',
    component: PurchaseBillsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/purchase-bills/:id',
    name: 'purchase-bill-detail',
    component: PurchaseBillDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/debit-notes',
    name: 'debit-notes',
    component: DebitNotesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/credit-notes',
    name: 'credit-notes',
    component: CreditNotesView,
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard - check authentication and module access
router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, fetchMe, hasModuleAccess, user } = useAuth()
  const { apiClient } = await import('@shared/utils/apiClient.js')

  // Route requires auth
  if (to.meta.requiresAuth) {
    // Check if token exists
    if (!apiClient.isAuthenticated()) {
      // No token - redirect to login
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Token exists, fetch user info if not loaded
    if (!user.value) {
      try {
        await fetchMe()
      } catch (error) {
        // Failed to fetch user (token invalid) - redirect to login
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
    }

    // Check billing module access
    if (!hasModuleAccess('billing')) {
      next({ name: 'no-access' })
      return
    }
  }

  // Already logged in and trying to access login page
  if (to.name === 'login' && isAuthenticated.value) {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
